// useLogout.ts - Improved logout hook with thorough Clerk session cleanup

import { useClerk } from '@clerk/clerk-react';
import { useCallback } from 'react';
import { apiService } from '../services/apiService';
import { isViewAsTab } from '../services/viewAsState';

export const useLogout = () => {
    const clerk = useClerk();

    const logout = useCallback(async (options?: { 
        redirectTo?: string;
        clearAllSessions?: boolean; 
    }) => {
        const { redirectTo, clearAllSessions = true } = options || {};
        // A View as member tab ends the view, and nothing else: the admin's
        // own sign-in (and their Clerk session, shared by every tab) stays.
        if (isViewAsTab()) {
            await apiService.endViewAs();
            return;
        }
        
        try {
            console.log('Starting logout process...');
            
            // 1. Clear your backend tokens FIRST
            // This ensures user is logged out of your system even if Clerk fails
            const userId = apiService.getStoredUserId();
            if (userId) {
                try {
                    await apiService.logout(userId, clearAllSessions);
                } catch (e) {
                    console.warn('Backend logout failed:', e);
                    // Continue anyway - we'll clear local state
                }
            }
            
            // clearStoredAuth() removes user_id, access_token, refresh_token and
            // last_activity together. This used to also call
            // clearStoredAccessToken / clearStoredRefreshToken / clearStoredUserId,
            // none of which exist on ApiService — written as `?.()` they were
            // silent no-ops that read like real cleanup, which is worse than
            // not being here at all.
            apiService.clearStoredAuth();
            apiService.clearStoredSessionId();
            
            // 2. Clear any other app-specific storage
            const keysToRemove = [
                'user',
                'auth_state',
                'user_profile',
                'session_data',
                'userPassword',
                'chat_session_id',
                'onboarding_seed',
                'banner_popup_dismissed',
            ];
            
            keysToRemove.forEach(key => {
                try {
                    localStorage.removeItem(key);
                    sessionStorage.removeItem(key);
                } catch (e) {
                    // Ignore storage errors
                }
            });

            // The per-user caches are keyed `<name>_<email>`, so the exact key
            // set isn't known ahead of time — match on the prefix. Without this,
            // one account's chats, messages and profile stayed readable in the
            // tab after signing out of it. sessionStorage does die with the tab,
            // but "close your browser" is not a logout guarantee.
            try {
                const perUserPrefixes = ['chats_', 'messages_', 'activeChatId_',
                    'activeConversationId_', 'inboxConversations_', 'userProfileData_',
                    'userRDProfileData_', 'userInterestsData_', 'chat_session_'];
                Object.keys(sessionStorage)
                    .filter(k => perUserPrefixes.some(prefix => k.startsWith(prefix)))
                    .forEach(k => sessionStorage.removeItem(k));
            } catch (e) {
                // Ignore storage errors
            }
            
            // 3. Sign out from Clerk - THIS IS CRITICAL
            // Only attempt if Clerk is available and loaded
            if (clerk && clerk.loaded) {
                try {
                    if (clearAllSessions) {
                        // Sign out from ALL sessions (all devices)
                        // This is more thorough but signs user out everywhere
                        await clerk.signOut({ sessionId: undefined });
                    } else if (clerk.session) {
                        // Sign out only current session
                        await clerk.signOut({ sessionId: clerk.session.id });
                    } else {
                        // Fallback - just call signOut
                        await clerk.signOut();
                    }
                } catch (clerkError) {
                    console.warn('Clerk signOut warning:', clerkError);
                    // Try alternative approach
                    try {
                        await clerk.signOut();
                    } catch (e) {
                        console.warn('Fallback signOut also failed:', e);
                    }
                }
            } else {
                // Clerk not available or not loaded - this is fine for non-Clerk logins
                console.log('Clerk not available - skipping Clerk signOut (user may have logged in via email/password)');
            }
            
            // 4. Clear Clerk-related cookies manually as a fallback
            // Clerk stores session data in cookies prefixed with __clerk
            clearClerkCookies();
            
            console.log('Logout successful');
            
            // 5. Redirect if specified
            if (redirectTo) {
                window.location.href = redirectTo;
            }
            
            return true;
        } catch (error) {
            console.error('Logout error:', error);
            
            // Even on error, try to clear local state
            apiService.clearStoredAuth();
            apiService.clearStoredSessionId();
            clearClerkCookies();
            
            // Still redirect if requested (user expects to be logged out)
            if (redirectTo) {
                window.location.href = redirectTo;
            }
            
            return false;
        }
    }, [clerk]);

    // Force clear all auth state - use this if normal logout doesn't work
    const forceLogout = useCallback(async () => {
        console.log('Force logout initiated...');
        
        // Clear everything aggressively
        apiService.clearStoredAuth();
        apiService.clearStoredSessionId();
        
        // Clear all localStorage
        try {
            const keysToCheck = Object.keys(localStorage);
            keysToCheck.forEach(key => {
                if (key.includes('clerk') || 
                    key.includes('auth') || 
                    key.includes('session') ||
                    key.includes('token') ||
                    key.includes('user')) {
                    localStorage.removeItem(key);
                }
            });
        } catch (e) {
            // Ignore
        }
        
        // Clear sessionStorage
        try {
            sessionStorage.clear();
        } catch (e) {
            // Ignore
        }
        
        // Clear all cookies
        clearClerkCookies();
        clearAllAuthCookies();
        
        // Try Clerk signOut
        try {
            if (clerk && clerk.loaded) {
                await clerk.signOut();
            }
        } catch (e) {
            console.warn('Force logout - Clerk signOut failed:', e);
        }
        
        // Hard refresh to clear any in-memory state
        window.location.href = '/';
    }, [clerk]);

    return { logout, forceLogout };
};

// Helper function to clear Clerk cookies
function clearClerkCookies() {
    try {
        const cookies = document.cookie.split(';');
        
        cookies.forEach(cookie => {
            const cookieName = cookie.split('=')[0].trim();
            
            // Clear Clerk-related cookies
            if (cookieName.startsWith('__clerk') || 
                cookieName.startsWith('__session') ||
                cookieName.startsWith('__client')) {
                
                // Clear for current path
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
                
                // Clear for root path
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
                
                // Try with different domain variations
                const domainParts = window.location.hostname.split('.');
                if (domainParts.length > 1) {
                    const rootDomain = domainParts.slice(-2).join('.');
                    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${rootDomain}`;
                }
            }
        });
    } catch (e) {
        console.warn('Error clearing Clerk cookies:', e);
    }
}

// Helper to clear all auth-related cookies
function clearAllAuthCookies() {
    try {
        const cookies = document.cookie.split(';');
        
        const authKeywords = ['auth', 'session', 'token', 'clerk', 'jwt', 'user'];
        
        cookies.forEach(cookie => {
            const cookieName = cookie.split('=')[0].trim().toLowerCase();
            
            if (authKeywords.some(keyword => cookieName.includes(keyword))) {
                document.cookie = `${cookie.split('=')[0].trim()}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
            }
        });
    } catch (e) {
        console.warn('Error clearing auth cookies:', e);
    }
}

export default useLogout;

